import { NavLink, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { motion } from 'framer-motion';
import { useUserRole } from '../context/UserRoleContext';
import { useProject, UNASSIGNED_PROJECT_FILTER } from '../context/ProjectContext';
import { clearPortalApiCache } from '../api/portalApi';
import { Icon } from './icons';
import ThemeToggle from './ThemeToggle';

const DEV_BYPASS = import.meta.env.VITE_DEV_BYPASS_AUTH === 'true';

const NAV_ITEMS = [
  { to: '/my-submissions', icon: 'grid',     label: 'Dashboard' },
  { to: '/generator',      icon: 'sparkle',  label: 'Generator' },
  { to: '/api-testing',    icon: 'play',     label: 'API Testing' },
  { to: '/web-automation', icon: 'robot',    label: 'Web Automation' },
  { to: '/releases',       icon: 'rocket',   label: 'Releases' },
  { to: '/settings',       icon: 'gear',     label: 'Settings' },
] as const;

export default function NavBar() {
  const { instance, accounts } = useMsal();
  const location  = useLocation();
  const account   = DEV_BYPASS ? null : accounts[0];
  const { isAdmin } = useUserRole();
  const { projects, activeProjectId, setActiveProjectId } = useProject();
  const activeProjects = projects.filter(p => p.status === 'active');
  const isDashboard = ['/', '/my-submissions', '/assigned', '/all-sessions'].some(
    p => location.pathname === p || location.pathname.startsWith(p + '/'),
  );
  const initials = account?.name
    ? account.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
    : DEV_BYPASS ? 'DV' : (account?.username?.[0] ?? '?').toUpperCase();

  return (
    <aside className="sidebar">
      <div className="sidebar-brand">
        <span className="sidebar-mark"><Icon name="sparkle" size={16} /></span>
        <span className="sidebar-brand-text">
          <span className="sidebar-wordmark">
            <span className="sidebar-logo">Fint</span>
            <span className="sidebar-product">QualiForge</span>
          </span>
          <span className="sidebar-tagline">FINT · AI Test Engineering</span>
        </span>
      </div>

      <div className="sidebar-section-label">Project</div>
      <div className="sidebar-project-picker">
        <select
          className="form-input"
          value={activeProjectId ?? ''}
          onChange={e => setActiveProjectId(e.target.value || null)}
        >
          <option value="">All Projects</option>
          <option value={UNASSIGNED_PROJECT_FILTER}>Unassigned</option>
          {activeProjects.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>

      <div className="sidebar-section-label">Menu</div>
      <nav className="sidebar-nav">
        <NavLink
          to="/my-submissions"
          className={`sidebar-link ${isDashboard ? 'sidebar-link-active' : ''}`}
        >
          {isDashboard && <motion.span className="sidebar-link-bg" layoutId="sidebar-active-bg" transition={{ duration: 0.2 }} />}
          <Icon name="grid" /><span>Dashboard</span>
        </NavLink>
        {NAV_ITEMS.slice(1).map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) => `sidebar-link ${isActive ? 'sidebar-link-active' : ''}`}
          >
            {({ isActive }) => (
              <>
                {isActive && <motion.span className="sidebar-link-bg" layoutId="sidebar-active-bg" transition={{ duration: 0.2 }} />}
                <Icon name={item.icon} /><span>{item.label}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="sidebar-footer">
        <span className="user-avatar" title={account?.username ?? 'Dev Mode'}>
          {initials}
          {isAdmin && <span className="admin-badge" title="Admin">A</span>}
        </span>
        <span className="sidebar-user-meta">
          <span className="sidebar-user-name">{DEV_BYPASS ? 'Dev User' : (account?.name || account?.username)}</span>
          <span className="sidebar-user-email">{DEV_BYPASS ? 'dev@fintinc.com' : account?.username}</span>
        </span>
        {DEV_BYPASS && <span className="dev-mode-badge">Mock</span>}
        <ThemeToggle />
        {!DEV_BYPASS && (
          <button className="btn-signout" title="Sign out" onClick={() => { clearPortalApiCache(); instance.logoutPopup(); }}>
            <Icon name="log-out" size={14} />
          </button>
        )}
      </div>
    </aside>
  );
}
